import { useEffect, useRef, useState, memo, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

const articles = [
  {
    slug: 'prevent-water-damage',
    image:
      'https://public.readdy.ai/ai/img_res/e9717219d832dbdc15bb3a6a7f3fb818.jpg',
    category: 'Prevention Tips',
    title: '10 Ways to Prevent Water Damage in Your Home',
    excerpt:
      'Learn the essential steps every homeowner should take to protect their property from costly water damage before it happens.',
    tips: [
      'Inspect pipes and hoses regularly',
      'Clean gutters and downspouts',
      'Know your water main location',
    ],
    icon: 'ri-shield-check-line',
  },
  {
    slug: 'water-leak-detection-systems',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20plumber%20installing%20a%20water%20leak%20detection%20sensor%20device%20near%20water%20heater%20in%20basement%20modern%20smart%20home%20technology%20clean%20organized%20utility%20room%20bright%20lighting&width=600&height=400&seq=blog2&orientation=landscape',
    category: 'Smart Home',
    title: 'Water Leak Detection Systems: A Complete Guide',
    excerpt:
      'Discover how smart water sensors can alert you to leaks early and potentially save thousands in repair costs.',
    tips: [
      'Install sensors near water heaters',
      'Place detectors under sinks',
      'Connect to smartphone alerts',
    ],
    icon: 'ri-sensor-line',
  },
  {
    slug: 'winterizing-prevent-frozen-pipes',
    image:
      'https://readdy.ai/api/search-image?query=Frozen%20pipe%20bursting%20with%20ice%20crystals%20forming%20on%20exterior%20home%20plumbing%20during%20winter%20cold%20weather%20damage%20prevention%20concept%20snowy%20background%20dramatic%20lighting&width=600&height=400&seq=blog3&orientation=landscape',
    category: 'Seasonal Care',
    title: 'Winterizing Your Home: Prevent Frozen Pipes',
    excerpt:
      'Utah winters can be brutal on your plumbing. Follow these steps to prevent frozen and burst pipes this season.',
    tips: [
      'Insulate exposed pipes',
      'Keep cabinet doors open',
      'Maintain consistent heating',
    ],
    icon: 'ri-snowy-line',
  },
];

const quickTips = [
  {
    icon: 'ri-water-flash-line',
    title: 'Check Water Pressure',
    description: 'High water pressure can stress pipes. Keep it between 40-80 PSI.',
  },
  {
    icon: 'ri-home-gear-line',
    title: 'Inspect Appliances',
    description: 'Check washing machine and dishwasher hoses every 6 months.',
  },
  {
    icon: 'ri-plant-line',
    title: 'Watch Tree Roots',
    description: 'Tree roots can damage underground pipes. Monitor growth near lines.',
  },
  {
    icon: 'ri-temp-cold-line',
    title: 'Maintain HVAC',
    description: 'Service your AC unit regularly to prevent condensation issues.',
  },
];

const ArticleCard = memo(({ article }: { article: typeof articles[0] }) => (
  <Link
    to={`/blog/${article.slug}`}
    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 cursor-pointer group"
  >
    <div className="relative h-44 sm:h-52 overflow-hidden">
      <img
        src={article.image}
        alt={article.title}
        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        loading="lazy"
        decoding="async"
      />
      <div className="absolute top-4 left-4">
        <span className="bg-sky-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
          {article.category}
        </span>
      </div>
    </div>
    <div className="p-5 sm:p-6">
      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 group-hover:text-sky-600 transition-colors">
        {article.title}
      </h3>
      <p className="text-gray-600 text-lg leading-relaxed mb-4">{article.excerpt}</p>
      <div className="border-t border-gray-100 pt-4">
        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
          Key Takeaways:
        </p>
        <ul className="space-y-1.5">
          {article.tips.map((tip, tipIndex) => (
            <li key={tipIndex} className="flex items-center gap-2 text-base text-gray-700">
              <i className="ri-check-line text-sky-600"></i>
              {tip}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </Link>
));

ArticleCard.displayName = 'ArticleCard';

const QuickTipCard = memo(({ tip }: { tip: typeof quickTips[0] }) => (
  <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-5 sm:p-6 text-center hover:bg-white/20 transition-all">
    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-sky-600/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
      <i className={`${tip.icon} text-xl sm:text-2xl text-sky-400`}></i>
    </div>
    <h4 className="text-base sm:text-lg font-bold text-white mb-2">{tip.title}</h4>
    <p className="text-gray-300 text-sm sm:text-base leading-relaxed">{tip.description}</p>
  </div>
));

QuickTipCard.displayName = 'QuickTipCard';

function Blog() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  const scrollToContact = useCallback(() => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => {
      const nameInput = document.getElementById('name') as HTMLInputElement;
      nameInput?.focus();
    }, 500);
  }, []);

  return (
    <section id="blog" ref={sectionRef} className="py-16 sm:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`text-center mb-10 sm:mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-sky-600 text-xl font-semibold mb-2">Resources &amp; Tips</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            Water Damage Prevention Guide
          </h2>
          <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto">
            Protect your home with expert advice from Utah&apos;s trusted restoration professionals.
            Prevention is always better than repair.
          </p>
        </div>

        {/* Featured Articles */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {articles.map((article, index) => (
            <ArticleCard key={index} article={article} />
          ))}
        </div>

        {/* View All Link */}
        <div
          className={`text-center mb-12 sm:mb-16 transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sky-600 text-sm font-semibold hover:text-sky-700 transition-colors cursor-pointer"
          >
            View All Articles
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Quick Tips Grid */}
        <div
          className={`bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-8 sm:mb-10">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-sky-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-lightbulb-flash-line text-2xl sm:text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">Quick Prevention Tips</h3>
            <p className="text-gray-300 text-base sm:text-lg max-w-xl mx-auto">
              Simple actions you can take today to protect your home from water damage
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {quickTips.map((tip, index) => (
              <QuickTipCard key={index} tip={tip} />
            ))}
          </div>
        </div>

        {/* Emergency CTA */}
        <div
          className={`mt-10 sm:mt-16 bg-white rounded-2xl p-6 sm:p-8 md:p-10 shadow-lg transition-all duration-1000 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex flex-col items-center text-center gap-6">
            <div className="flex items-center gap-4 sm:gap-5">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-alarm-warning-line text-2xl sm:text-3xl text-red-600"></i>
              </div>
              <div className="text-left">
                <h4 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 mb-2">
                  Already Have Water Damage?
                </h4>
                <p className="text-gray-600 text-base sm:text-lg">
                  Don&apos;t wait — water damage gets worse every hour. Call us now for immediate help.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
              <button
                onClick={scrollToContact}
                className="w-full sm:w-auto bg-sky-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-sky-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Get Your Free Assessment
              </button>
              <a
                href="tel:3853406632"
                className="w-full sm:w-auto text-center border border-gray-300 text-gray-900 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                Call Now: (385) 340-6632
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default memo(Blog);
